import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { MegaMenuLink } from './MegaMenuLink';
import { MegaMenuVisuals } from './MegaMenuVisuals';
import { MegaMenuCSSCustomProperties, fadeTransitions } from './MegaMenu.config';
import { linkStyle, labelTextStyle, sectionLayoutStyles } from './MegaMenu.styles';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { useMegaMenu } from './MegaMenuContext';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import IconButton from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import { MegaMenuScrollHinter } from './MegaMenuScrollHinter';
import { createCSSPropertyTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useFocusInitial, useFocusRestore, useFocusTrap, } from '@polestar/component-warehouse-react/hooks/focus';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import Alignments from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import { css } from '@emotion/react';
import useTrackingData, { formatTrackingLabel, } from '@polestar/component-warehouse-react/hooks/useTrackingData';
import { DataLayerAction, DataLayerCategory, DataLayerEvent, DataLayerLabel, } from '@polestar/component-warehouse-react/tokens/analytics/Analytics';
import { StaticCloseButton } from './MegaMenuSharedCloseButton';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
var MegaMenuSecondaryNavigationPanelCSSCustomProp;
(function (MegaMenuSecondaryNavigationPanelCSSCustomProp) {
    MegaMenuSecondaryNavigationPanelCSSCustomProp["panelOpacity"] = "--cw-mega-menu-panel-opacity";
    MegaMenuSecondaryNavigationPanelCSSCustomProp["headingGap"] = "--cw-mega-menu-heading-gap";
})(MegaMenuSecondaryNavigationPanelCSSCustomProp || (MegaMenuSecondaryNavigationPanelCSSCustomProp = {}));
export const MegaMenuSecondaryNavigationPanel = ({ id, navigationPanelIndex, panelContent, backButtonLabel, ariaLabels, }) => {
    var _a, _b;
    const { activeFlyoutIndex, setFlyoutActive, setMenuActive, flyoutItems, mainMenuItems, setFlyoutHasFocus, flyoutActive, setActiveFlyoutIndex, } = useMegaMenu();
    const panelRef = useRef(null);
    // only support focus trap on mobile/tablet keyboard mode
    const { isKeyboardControlled } = useInteractionMode();
    const { name } = useMediaQuery();
    const nonDesktopDevice = name === DeviceTypes.mobile || name === DeviceTypes.tablet;
    const supportFocusTrap = isKeyboardControlled && nonDesktopDevice;
    const tabIndex = flyoutActive ? 0 : -1;
    const isActive = navigationPanelIndex === activeFlyoutIndex;
    const onLinkKeyDown = (event) => {
        // Seems to be desktop behavior that needs to be prevented
        const currentSubmenuItems = flyoutItems[navigationPanelIndex];
        const currentLink = event.currentTarget;
        if (currentLink === currentSubmenuItems[0] && event.key === 'Tab' && event.shiftKey) {
            event.preventDefault();
            setFlyoutHasFocus(false);
            mainMenuItems[activeFlyoutIndex].focus();
        }
        else if (currentLink === currentSubmenuItems[currentSubmenuItems.length - 1] &&
            event.key === 'Tab' &&
            !event.shiftKey) {
            const nextItem = mainMenuItems[activeFlyoutIndex + 1];
            // todo: use for link
            // if (nextItem) {
            //   event.preventDefault();
            //   nextItem.focus();
            //   setFlyoutHasFocus(false);
            // } else {
            //   setMenuActive(false);
            //   setFlyoutActive(false);
            // }
            if (nextItem) {
                event.preventDefault();
                // only for flyout
                !supportFocusTrap && nextItem.focus();
                setFlyoutHasFocus(false);
                if (nextItem.nodeName.toLowerCase() !== 'button') {
                    // is last item
                    if (nonDesktopDevice)
                        return;
                    setFlyoutActive(false);
                    setActiveFlyoutIndex(null);
                    // TODO: find a way to fix this timeout
                    // After going hitting tab on the last item in the flyout, the focus should go to the next main menu item
                    // This is in this case the Action items
                    // Without the timeout it will not work
                    setTimeout(() => {
                        nextItem.focus();
                    }, 0);
                }
            }
            else {
                setMenuActive(false);
                setFlyoutActive(false);
            }
        }
    };
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(panelRef, internalRef);
    useFocusInitial(internalRef, supportFocusTrap && isActive, { scrollIntoView: false });
    useFocusTrap(internalRef, supportFocusTrap && isActive);
    useFocusRestore(supportFocusTrap);
    // tracking
    const { pushDataLayer } = useTrackingData();
    const handleActionClick = (e, label, 
    // TODO: works the same for touch?
    onClick) => {
        // TODO: not yet triggered, why?
        pushDataLayer({
            event: DataLayerEvent.LINK_CLICK,
            category: DataLayerCategory.MENU,
            action: DataLayerAction.LINK_CLICK,
            // TODO: en naming, how?
            // TODO: how to switch between toggles?
            label: formatTrackingLabel(label),
        });
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    const handleBackClick = () => {
        mainMenuItems[activeFlyoutIndex].focus();
        setFlyoutHasFocus(false);
        setFlyoutActive(false);
        setActiveFlyoutIndex(null);
        // tracking
        pushDataLayer({
            event: DataLayerEvent.LINK_CLICK,
            category: DataLayerCategory.MENU,
            action: DataLayerAction.MENU_CLICK,
            label: DataLayerLabel.MENU_BACK,
        });
    };
    // tracking
    const handleHeadingActionClick = (e, label) => {
        pushDataLayer({
            event: DataLayerEvent.LINK_CLICK,
            category: DataLayerCategory.MENU,
            action: DataLayerAction.LINK_CLICK,
            // TODO: en naming, how? eventLabel
            label: formatTrackingLabel(label),
        });
    };
    return (React.createElement(Panel, { style: {
            [MegaMenuSecondaryNavigationPanelCSSCustomProp.panelOpacity]: isActive ? 1 : 0,
            [MegaMenuSecondaryNavigationPanelCSSCustomProp.headingGap]: ((_a = panelContent === null || panelContent === void 0 ? void 0 : panelContent.links) === null || _a === void 0 ? void 0 : _a.length) === 1 ? Spacings.large : '0',
            pointerEvents: isActive ? 'auto' : 'none',
        }, ref: combinedRef, id: id },
        React.createElement(PanelHeader, null,
            React.createElement("span", null, panelContent === null || panelContent === void 0 ? void 0 : panelContent.panelName)),
        React.createElement(PanelContent, null,
            React.createElement(MegaMenuScrollHinter, { isSecondary: true }, (_b = panelContent === null || panelContent === void 0 ? void 0 : panelContent.links) === null || _b === void 0 ? void 0 :
                _b.map((links, index) => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    return (React.createElement(Column, { key: index, style: {} },
                        !!(panelContent === null || panelContent === void 0 ? void 0 : panelContent.heading) && index === 0 && (React.createElement(Heading, null,
                            React.createElement(HeadingTitle, { ...aria({ hidden: true }) }, (_a = panelContent === null || panelContent === void 0 ? void 0 : panelContent.heading) === null || _a === void 0 ? void 0 : _a.label),
                            React.createElement(MegaMenuLink, { "data-type": "secondary-link", href: (_c = (_b = panelContent === null || panelContent === void 0 ? void 0 : panelContent.heading) === null || _b === void 0 ? void 0 : _b.link) === null || _c === void 0 ? void 0 : _c.href, onKeyDown: (event) => onLinkKeyDown(event), tabIndex: tabIndex, onClick: (e) => {
                                    var _a, _b;
                                    handleHeadingActionClick(e, (_b = (_a = panelContent === null || panelContent === void 0 ? void 0 : panelContent.heading) === null || _a === void 0 ? void 0 : _a.link) === null || _b === void 0 ? void 0 : _b.eventLabel);
                                }, ...aria({
                                    hidden: !flyoutActive,
                                    label: (_e = (_d = panelContent === null || panelContent === void 0 ? void 0 : panelContent.heading) === null || _d === void 0 ? void 0 : _d.link) === null || _e === void 0 ? void 0 : _e['aria-label'],
                                }), 
                                // onKeyDown={(event) => handleKeyDown(event, true)}
                                label: (_g = (_f = panelContent === null || panelContent === void 0 ? void 0 : panelContent.heading) === null || _f === void 0 ? void 0 : _f.link) === null || _g === void 0 ? void 0 : _g.label }))),
                        React.createElement(Links, null, links.map((link, index) => (React.createElement(MegaMenuLink, { tabIndex: tabIndex, key: index, "data-type": "secondary-link", ...link, onKeyDown: (event) => onLinkKeyDown(event), onClick: (e) => {
                                handleActionClick(e, formatTrackingLabel(link === null || link === void 0 ? void 0 : link.eventLabel), link === null || link === void 0 ? void 0 : link.onClick);
                            }, ...aria({
                                hidden: !flyoutActive,
                            }) }))))));
                }),
                (panelContent === null || panelContent === void 0 ? void 0 : panelContent.visuals) && (React.createElement(VisualWrapper, null,
                    React.createElement(MegaMenuVisuals, { visuals: panelContent === null || panelContent === void 0 ? void 0 : panelContent.visuals }))))),
        React.createElement(PanelFooter, null,
            React.createElement(IconButton, { "data-type": "back-button", onClick: handleBackClick, ...aria({ label: backButtonLabel }), size: Sizes.large, icon: Icons.arrowLeft, tabIndex: tabIndex, alignmentOffset: Alignments.left })),
        nonDesktopDevice && (React.createElement(StaticCloseButton, { "aria-label": ariaLabels.closeMenu, hideOnPointer: true, tabIndex: tabIndex }))));
};
const flexColumnStyle = css `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const Panel = styled.div `
  ${sectionLayoutStyles};
  --gap: ${Spacings.large}; /* used for the flex and grid stuff */
  block-size: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr auto;
  background: var(${MegaMenuCSSCustomProperties.background});
  color: var(${MegaMenuCSSCustomProperties.color});
  opacity: var(${MegaMenuSecondaryNavigationPanelCSSCustomProp.panelOpacity});
  /* TODO: some sort of race-condition here: */
  transition: ${createCSSPropertyTransition('opacity', fadeTransitions)};

  ${MediaQueries.desktop} {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    background: transparent;
  }

  &:has(a:hover, a:focus-within) {
    a:not(:hover, a:focus-within) {
      opacity: 0.6;
    }
  }
`;
const PanelHeader = styled.header `
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${createCustomSpacing(19 / 16)} 0;

  ${labelTextStyle};

  ${MediaQueries.tablet} {
    padding-block: ${createCustomSpacing(23 / 16)};
  }

  ${MediaQueries.desktop} {
    display: none;
  }
`;
const PanelContent = styled.div `
  position: relative;
  flex-grow: 1;
  max-block-size: 100%;
  min-block-size: 0;

  ${MediaQueries.desktop} {
    padding: 0 0 ${Spacings.xxxLarge};
    display: flex;
    justify-content: center;
  }
`;
const VisualWrapper = styled.div `
  display: none;

  ${MediaQueries.desktop} {
    display: block;
    grid-row: 1 / span 2;
    grid-column: 9 / span 5;
    margin-inline-start: calc(${Spacings.xLarge} * -1);
  }

  ${MediaQueries.desktopWide} {
    grid-column: 9 / span 6;
  }
`;
const PanelFooter = styled.footer `
  padding-block: ${Spacings.small};

  ${MediaQueries.desktop} {
    display: none;
  }
`;
const Heading = styled.div `
  ${flexColumnStyle};
`;
const HeadingTitle = styled.div `
  ${linkStyle};
  opacity: ${Opacities.medium};

  ${MediaQueries.desktop} {
    display: none;
  }
`;
const Links = styled.div `
  ${flexColumnStyle};
`;
const Column = styled.div `
  display: flex;
  flex-direction: column;
  gap: var(${MegaMenuSecondaryNavigationPanelCSSCustomProp.headingGap}, 0);

  ${MediaQueries.tablet} {
    margin-block-end: 0;
  }

  ${MediaQueries.desktop} {
    grid-row-start: 2;
  }
`;
// const StyledAnchor = styled(Link)`
//   ${linkStyle};
//   text-decoration: none;
//   padding-block: ${createCustomSpacing(5 / 16)};
// `;
